/**
 * Cross-app options for online-only exchange and digital-asset settlement.
 * Prefer these catalogs via optionsSource so Buy/Sell, Rentals, xChange,
 * Services, Events, and Payer & Delivery stay aligned.
 */

/** How the parties meet / complete the deal */
export const EXCHANGE_MODE_OPTIONS = [
  { value: 'in_person', label: 'In person / meetup' },
  { value: 'online_only', label: 'Online only (remote)' },
  { value: 'either', label: 'Either — flexible' },
];

/** What is being exchanged */
export const ASSET_KIND_OPTIONS = [
  { value: 'physical', label: 'Physical goods' },
  { value: 'digital', label: 'Digital goods / files / licenses' },
  { value: 'funds', label: 'Funds / balances only' },
  { value: 'service', label: 'Service / access' },
  { value: 'mixed', label: 'Mixed (physical + digital)' },
];

/** How value moves — cash, rails, or locked digital assets */
export const SETTLEMENT_METHOD_OPTIONS = [
  { id: 'cash', label: 'Cash' },
  { id: 'bank_transfer', label: 'Bank transfer' },
  { id: 'mobile_wallet', label: 'Mobile money / e-wallet' },
  { id: 'stablecoin', label: 'Stablecoin (USDC / USDT)' },
  { id: 'crypto', label: 'Other crypto / digital asset' },
  { id: 'locked_escrow', label: 'Lock in non-custodial escrow' },
  { id: 'card', label: 'Card' },
];

/** Online-only fulfillment / delivery of the thing paid for */
export const ONLINE_FULFILLMENT_OPTIONS = [
  { id: 'wallet_transfer', label: 'Wallet-to-wallet transfer' },
  { id: 'escrow_lock', label: 'Escrow lock → release on confirm' },
  { id: 'download_link', label: 'Download / access link' },
  { id: 'license_key', label: 'License key / code / voucher' },
  { id: 'account_credit', label: 'Account credit / top-up' },
  { id: 'remote_session', label: 'Remote session (call / screen share)' },
  { id: 'email_delivery', label: 'Email / chat delivery' },
  { id: 'streaming_access', label: 'Live / streaming access' },
];

/** Fiat + digital asset units commonly posted */
export const SETTLEMENT_CURRENCY_OPTIONS = [
  { value: 'JMD', label: 'JMD' },
  { value: 'USD', label: 'USD' },
  { value: 'USDC', label: 'USDC' },
  { value: 'USDT', label: 'USDT' },
  { value: 'BTC', label: 'BTC' },
  { value: 'ETH', label: 'ETH' },
  { value: 'other_crypto', label: 'Other crypto' },
  { value: 'other', label: 'Other' },
];

/** xChange directions */
export const XCHANGE_DIRECTION_OPTIONS = [
  { value: 'sell_crypto', label: 'Sell crypto / digital asset for cash' },
  { value: 'buy_crypto', label: 'Buy crypto / digital asset with cash' },
  { value: 'send_funds', label: 'Send funds online to someone' },
  { value: 'receive_funds', label: 'Receive funds online' },
  { value: 'swap_assets', label: 'Swap digital assets (A → B)' },
  { value: 'pay_for_goods', label: 'Pay online for goods or a service' },
];

export const XCHANGE_ASSET_OPTIONS = [
  { value: 'usdc', label: 'USDC' },
  { value: 'usdt', label: 'USDT' },
  { value: 'btc', label: 'BTC' },
  { value: 'eth', label: 'ETH' },
  { value: 'jmd_cash', label: 'JMD cash' },
  { value: 'usd_cash', label: 'USD cash' },
  { value: 'bank_jmd', label: 'JMD bank balance' },
  { value: 'mobile_money', label: 'Mobile money' },
  { value: 'other_crypto', label: 'Other crypto / token' },
  { value: 'other', label: 'Other (describe in notes)' },
];

export const XCHANGE_PROOF_OPTIONS = [
  { id: 'tx_hash', label: 'On-chain tx / hash' },
  { id: 'transfer_screenshot', label: 'Transfer screenshot' },
  { id: 'video_call', label: 'Live video confirm' },
  { id: 'escrow_pin', label: 'Escrow / handoff PIN' },
  { id: 'receipt', label: 'Receipt / statement' },
];

/** Events */
export const EVENT_FORMAT_OPTIONS = [
  { value: 'in_person', label: 'In person' },
  { value: 'online', label: 'Online only' },
  { value: 'hybrid', label: 'Hybrid (in person + online)' },
];

export const EVENT_CATEGORY_OPTIONS = [
  { id: 'meetup', label: 'Meetup / networking' },
  { id: 'market', label: 'Market / fair' },
  { id: 'workshop', label: 'Workshop / class' },
  { id: 'concert', label: 'Music / performance' },
  { id: 'sports', label: 'Sports / fitness' },
  { id: 'faith', label: 'Faith / community' },
  { id: 'business', label: 'Business / founders' },
  { id: 'crypto', label: 'Crypto / Web3' },
  { id: 'family', label: 'Family / kids' },
  { id: 'fundraiser', label: 'Fundraiser' },
  { id: 'other', label: 'Other' },
];

export const EVENT_ADMISSION_OPTIONS = [
  { value: 'free', label: 'Free' },
  { value: 'donation', label: 'Donation / pay what you can' },
  { value: 'paid', label: 'Paid ticket' },
  { value: 'members', label: 'Members / invite only' },
];

export const EVENT_TICKET_DELIVERY_OPTIONS = [
  { id: 'door', label: 'At the door' },
  { id: 'digital_pass', label: 'Digital pass / QR' },
  { id: 'online_link', label: 'Online access link' },
  { id: 'escrow_pin', label: 'Access PIN after payment' },
  { id: 'email', label: 'Email confirmation' },
];

/** Services delivery */
export const SERVICE_DELIVERY_OPTIONS = [
  { value: 'on_site', label: 'On-site / in person' },
  { value: 'remote_online', label: 'Remote / online only' },
  { value: 'either', label: 'Either — flexible' },
];

export const SERVICE_DELIVERY_MULTI_OPTIONS = [
  { id: 'on_site', label: 'On-site / visit' },
  { id: 'remote_online', label: 'Remote / online' },
  { id: 'shop', label: 'At my shop / studio' },
];

export const SERVICE_URGENCY_OPTIONS = [
  { value: 'flexible', label: 'Flexible' },
  { value: 'this_week', label: 'This week' },
  { value: 'urgent', label: 'Urgent / ASAP' },
  { value: 'scheduled', label: 'Specific date (say when)' },
];

export const EXCHANGE_TERMS_SUMMARY =
  'I confirm this request is lawful, the amounts and assets described are accurate, ' +
  'and I will not use the marketplace for prohibited, fraudulent, or stolen value. ' +
  'Online and escrow options are non-custodial when used — I understand keys stay with me.';

export const EVENT_TERMS_SUMMARY =
  'I confirm this event is lawful to host, admission and access details are accurate, ' +
  'and online tickets or PINs will only be issued for legitimate attendees.';

export const SERVICE_TERMS_SUMMARY =
  'I confirm this service request or offering is lawful, description is accurate, ' +
  'and remote/online work will not involve prohibited access or fraud.';

export default {
  EXCHANGE_MODE_OPTIONS,
  SETTLEMENT_METHOD_OPTIONS,
  ONLINE_FULFILLMENT_OPTIONS,
};
